import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { CommunityLayout } from '../components/CommunityLayout';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Card, Button } from '../components';
import { Users, MapPin, Plus, Filter, Search } from 'lucide-react';
import { intervisieApi, IntervisieGroup } from '../services/intervisieApi';

const STATUS_CLASSES: Record<string, string> = {
  beschikbaar: 'bg-green-100 text-green-700 border-green-200',
  bijna_vol: 'bg-yellow-100 text-yellow-700 border-yellow-200',
  vol: 'bg-red-100 text-red-700 border-red-200',
};

export const IntervisieGroepenPage: React.FC = () => {
  const { t } = useTranslation('pages');
  const navigate = useNavigate();
  const [locatie, setLocatie] = useState('');
  const [provincie, setProvincie] = useState('');
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);
  const [groupNumber, setGroupNumber] = useState('');
  const [doelgroep, setDoelgroep] = useState('');
  const [appliedFilters, setAppliedFilters] = useState<{
    locatie: string;
    provincie: string;
    status: string[];
    group_number: string;
    doelgroep: string;
  }>({ locatie: '', provincie: '', status: [], group_number: '', doelgroep: '' });

  const { data: options } = useQuery({
    queryKey: ['intervisie-options'],
    queryFn: intervisieApi.getOptions,
  });

  const { data: result, isLoading } = useQuery({
    queryKey: ['intervisie-groups', appliedFilters],
    queryFn: () =>
      intervisieApi.getGroups({
        locatie: appliedFilters.locatie || undefined,
        provincie: appliedFilters.provincie || undefined,
        status: appliedFilters.status.length > 0 ? appliedFilters.status : undefined,
        group_number: appliedFilters.group_number || undefined,
        doelgroep: appliedFilters.doelgroep || undefined,
        limit: 50,
      }),
  });

  const groups = result?.data || [];
  const total = result?.total || 0;

  const STATUS_LABELS: Record<string, string> = {
    beschikbaar: t('intervisie.statusAvailable'),
    bijna_vol: t('intervisie.statusAlmostFull'),
    vol: t('intervisie.statusFull'),
  };

  const toggleStatus = (s: string) => {
    setSelectedStatuses((prev) =>
      prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]
    );
  };

  const applyFilters = () => {
    setAppliedFilters({
      locatie,
      provincie,
      status: selectedStatuses,
      group_number: groupNumber,
      doelgroep,
    });
  };

  const clearFilters = () => {
    setLocatie('');
    setProvincie('');
    setSelectedStatuses([]);
    setGroupNumber('');
    setDoelgroep('');
    setAppliedFilters({ locatie: '', provincie: '', status: [], group_number: '', doelgroep: '' });
  };

  return (
    <CommunityLayout>
    <div className="py-8 ml-8">
      {/* Header */}
      <div className="mb-6">
        <div className="flex justify-end">
          <Button onClick={() => navigate('/intervisie/aanmelden')}>
            <Plus className="w-4 h-4 mr-2" />
            {t('intervisie.newGroup')}
          </Button>
        </div>

        {/* Info banner */}
        <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-800">
          <p>
            <strong>{t('intervisie.registrationInfoTitle')}</strong>{' '}
            {t('intervisie.registrationInfoText')}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Filters sidebar */}
        <div className="lg:col-span-1">
          <Card>
            <h2 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Filter className="w-4 h-4" />
              {t('intervisie.filters')}
            </h2>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('intervisie.groupNumber')}
                </label>
                <input
                  type="text"
                  value={groupNumber}
                  onChange={(e) => setGroupNumber(e.target.value)}
                  placeholder={t('intervisie.groupNumberPlaceholder')}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0B1F3A]"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('intervisie.location')}
                </label>
                <input
                  type="text"
                  value={locatie}
                  onChange={(e) => setLocatie(e.target.value)}
                  placeholder={t('intervisie.locationPlaceholder')}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0B1F3A]"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('intervisie.province')}
                </label>
                <select
                  value={provincie}
                  onChange={(e) => setProvincie(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0B1F3A]"
                >
                  <option value="">{t('intervisie.allProvinces')}</option>
                  {(options?.provincies || []).map((p) => (
                    <option key={p} value={p}>{p}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('intervisie.targetGroup')}
                </label>
                <select
                  value={doelgroep}
                  onChange={(e) => setDoelgroep(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0B1F3A]"
                >
                  <option value="">{t('intervisie.allRoles')}</option>
                  {(options?.doelgroep_options || []).map((opt) => (
                    <option key={opt.value} value={opt.value}>{t(`screening.types.${opt.value}`, { defaultValue: opt.label })}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('intervisie.status')}
                </label>
                <div className="space-y-2">
                  {Object.keys(STATUS_LABELS).map((key) => (
                    <label key={key} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={selectedStatuses.includes(key)}
                        onChange={() => toggleStatus(key)}
                        className="rounded border-gray-300 text-[#0B1F3A] focus:ring-[#0B1F3A]"
                      />
                      <span className="text-sm text-gray-700">{STATUS_LABELS[key]}</span>
                    </label>
                  ))}
                </div>
              </div>

              <Button className="w-full" onClick={applyFilters}>
                <Search className="w-4 h-4 mr-2" />
                {t('intervisie.filter')}
              </Button>
              {(appliedFilters.locatie ||
                appliedFilters.provincie ||
                appliedFilters.status.length > 0 ||
                appliedFilters.group_number ||
                appliedFilters.doelgroep) && (
                <Button variant="outline" className="w-full" onClick={clearFilters}>
                  {t('intervisie.clearFilters')}
                </Button>
              )}
            </div>
          </Card>
        </div>

        {/* Groups list */}
        <div className="lg:col-span-3">
          {isLoading ? (
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="bg-white border border-gray-200 rounded-lg p-4 animate-pulse">
                  <div className="h-4 bg-gray-200 rounded w-1/2 mb-2" />
                  <div className="h-3 bg-gray-100 rounded w-1/3" />
                </div>
              ))}
            </div>
          ) : groups.length === 0 ? (
            <Card>
              <div className="text-center py-12">
                <Users className="mx-auto h-12 w-12 text-gray-300 mb-3" />
                <p className="text-gray-500 font-medium">{t('intervisie.noGroups')}</p>
                <p className="text-sm text-gray-400 mt-1">{t('intervisie.noGroupsHint')}</p>
                <Button className="mt-4" onClick={() => navigate('/intervisie/aanmelden')}>
                  <Plus className="w-4 h-4 mr-2" />
                  {t('intervisie.newGroup')}
                </Button>
              </div>
            </Card>
          ) : (
            <>
              <p className="text-sm text-gray-500 mb-3">
                {t('intervisie.groupsFound', { count: total })}
              </p>
              {/* Table */}
              <div className="overflow-x-hidden">
                <table className="w-full bg-white border border-gray-200 rounded-lg overflow-hidden">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-200">
                      <th className="text-left text-xs font-semibold text-gray-500 uppercase px-4 py-3">
                        {t('intervisie.colGroup')}
                      </th>
                      <th className="text-left text-xs font-semibold text-gray-500 uppercase px-4 py-3">
                        {t('intervisie.colLocation')}
                      </th>
                      <th className="text-left text-xs font-semibold text-gray-500 uppercase px-4 py-3">
                        {t('intervisie.colName')}
                      </th>
                      <th className="text-left text-xs font-semibold text-gray-500 uppercase px-4 py-3">
                        {t('intervisie.colTargetGroup')}
                      </th>
                      <th className="text-left text-xs font-semibold text-gray-500 uppercase px-4 py-3">
                        {t('intervisie.colContact')}
                      </th>
                      <th className="text-left text-xs font-semibold text-gray-500 uppercase px-4 py-3">
                        {t('intervisie.colStatus')}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {groups.map((group) => (
                      <GroupRow key={group.id} group={group} statusLabels={STATUS_LABELS} />
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
    </CommunityLayout>
  );
};

const GroupRow: React.FC<{ group: IntervisieGroup; statusLabels: Record<string, string> }> = ({
  group,
  statusLabels,
}) => {
  const { t } = useTranslation('pages');
  const statusClass = STATUS_CLASSES[group.status] || STATUS_CLASSES.beschikbaar;
  const statusLabel = statusLabels[group.status] || group.status;

  return (
    <tr className="hover:bg-gray-50 transition-colors">
      <td className="px-4 py-3 text-sm font-mono text-gray-600">{group.group_number}</td>
      <td className="px-4 py-3 text-sm text-gray-700">
        <div className="flex items-center gap-1">
          <MapPin className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
          {group.is_online ? (
            <span className="text-[#0B1F3A]">{t('intervisie.online')}</span>
          ) : (
            group.plaats || '—'
          )}
        </div>
      </td>
      <td className="px-4 py-3">
        <Link
          to={`/intervisie/${group.id}`}
          className="text-sm font-medium text-[#0B1F3A] hover:underline"
        >
          {group.name}
        </Link>
        {group.planning && (
          <div className="text-xs text-gray-400 mt-0.5">{group.planning}</div>
        )}
      </td>
      <td className="px-4 py-3 text-sm text-gray-600">
        {group.doelgroep ? t(`screening.types.${group.doelgroep}`, { defaultValue: group.doelgroep }) : '—'}
      </td>
      <td className="px-4 py-3 text-sm text-gray-700">
        {group.contact_first_name} {group.contact_last_name}
      </td>
      <td className="px-4 py-3">
        <span
          className={`inline-block text-xs font-medium px-2.5 py-1 rounded-full border ${statusClass}`}
        >
          {statusLabel}
        </span>
      </td>
    </tr>
  );
};
